import type { EmailCampaignId } from 'types/campaign'
import type { Thunk } from 'types/redux'
import { campaignListGetSingle } from '@disco/state-management/actions/campaign-list-actions'
import api from '@disco/api/server'
import {
  CAMPAIGN_STATS_LOAD,
  CAMPAIGN_STATS_LOAD_FAIL,
  CAMPAIGN_STATS_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
import { getCampaign } from '@disco/state-management/selectors/campaigns'

/*
const mock = {
  total_recipients: 1,
  open_rate: 76.2,
  click_rate: 18.3,
  number_of_opens: 1,
  number_of_clicks: 1,
  total_streams: 1,
  total_track_downloads: 1,
  total_playlist_downloads: 1,
  most_engaged_users: [
    {
      user: 'foo@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 2,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
    {
      user: 'you@foo.com',
      playlist_downloads: 1,
      track_downloads: 2,
      page_opens: 1,
      streams: 1,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
    {
      user: 'me@foo.com',
      playlist_downloads: 3,
      track_downloads: 2,
      page_opens: 1,
      streams: 4,
    },
  ],
  most_streamed_tracks: [
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
    { id: 333, name: 'Boo', artist: 'John Joe', count: 1234 },
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
  ],
  most_downloaded_tracks: [
    { id: 333, name: 'Foo', artist: 'John Doe', count: 1234 },
    { id: 333, name: 'You', artist: 'John Joe', count: 1234 },
  ],
  most_clicked_links: [
    {
      link:
        'http://example.disco.ac/upload/2249/inbox/?signature=6MEVDlzOxJU9XAtCw4hAUsD',
      clicks: 332,
    },
    {
      link: 'https://disco.ac',
      clicks: 255,
    },
    {
      link: 'https://google.com',
      clicks: 24,
    },
  ],
  change_from_last_campaign: {
    click_rate: 10,
    open_rate: 50,
    number_of_opens: 25,
    number_of_clicks: 10,
    total_streams: 30,
    total_track_downloads: 30,
    total_playlist_downloads: 30,
  },
}
*/
/**
 * Loads statistics for a specific email campaign.
 * @param campaignId - ID of the campaign to load stats for
 * @param fullStats - Whether to load full detailed statistics or basic stats
 * @returns Thunk that loads campaign statistics and handles success/error states
 */
export const loadCampaignStats =
  (campaignId: EmailCampaignId, fullStats: boolean): Thunk =>
  async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId,
    })

    if (!campaign || !campaign.template_data) {
      await dispatch(campaignListGetSingle(campaignId))
    }

    dispatch({
      type: CAMPAIGN_STATS_LOAD,
      campaignId,
    })

    try {
      const result = await api.getCampaignStats(campaignId, fullStats)
      return dispatch({
        type: CAMPAIGN_STATS_LOAD_SUCCESS,
        campaignId,
        result,
        fullStats,
      })
    } catch (error) {
      return dispatch({
        type: CAMPAIGN_STATS_LOAD_FAIL,
        campaignId,
        error,
      })
    }
  }
